import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { LIST_PAGE_SIZE_OPTIONS } from '@/hooks/useClientListMode';

/**
 * UV-style list controls: Alle | Seitenweise + page size + prev/next.
 * Pair with useClientListMode for client-side lists.
 */
export default function ListModePager({
  listMode,
  setListMode,
  pageSize,
  setPageSize,
  listPage,
  setListPage,
  totalCount,
  pageCount,
  visibleCount,
  label = 'Einträge',
  testId = 'list-mode-pager',
  trailing = null,
}) {
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 border-b border-slate-200 bg-slate-50 text-xs"
      data-testid={testId}
    >
      <div className="text-slate-600">
        Anzahl: <span className="font-bold text-slate-800">{visibleCount ?? totalCount}</span>
        {listMode === 'paged' && totalCount > 0 && (
          <span className="ml-2 text-slate-400">von {totalCount}</span>
        )}
        {listMode === 'all' && totalCount > 0 && (
          <span className="ml-2 text-emerald-700">(alle)</span>
        )}
        <span className="ml-1 text-slate-400">{label}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded border border-slate-200 overflow-hidden">
          <button
            type="button"
            data-testid={`${testId}-mode-all`}
            className={`px-2 py-1 ${listMode === 'all' ? 'bg-brand-red text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            onClick={() => {
              setListPage(1);
              setListMode('all');
            }}
            title="Alle Treffer anzeigen und durchscrollen"
          >
            Alle
          </button>
          <button
            type="button"
            data-testid={`${testId}-mode-paged`}
            className={`px-2 py-1 border-l border-slate-200 ${listMode === 'paged' ? 'bg-brand-red text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            onClick={() => {
              setListPage(1);
              setListMode('paged');
            }}
            title="Seitenweise mit wählbarer Seitengröße"
          >
            Seitenweise
          </button>
        </div>
        {listMode === 'paged' && (
          <>
            <label className="flex items-center gap-1 text-slate-500">
              max.
              <select
                data-testid={`${testId}-page-size`}
                className="h-7 rounded border border-slate-300 bg-white px-1 text-xs"
                value={pageSize}
                onChange={(e) => {
                  setListPage(1);
                  setPageSize(Number(e.target.value));
                }}
              >
                {LIST_PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid={`${testId}-page-prev`}
                disabled={listPage <= 1}
                className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-200 bg-white disabled:opacity-40"
                onClick={() => setListPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="tabular-nums text-slate-600 min-w-[4.5rem] text-center">
                {listPage} / {pageCount}
              </span>
              <button
                type="button"
                data-testid={`${testId}-page-next`}
                disabled={listPage >= pageCount}
                className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-200 bg-white disabled:opacity-40"
                onClick={() => setListPage((p) => Math.min(pageCount, p + 1))}
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        )}
        {trailing}
      </div>
    </div>
  );
}
